<template>
  <div class="customers">
    <div class="page__header">
      <div class="page__title">{{ $t(`pageTitles.${"leads"}`) }}</div>
      <div class="page__right"> 
         
        <div class="selected-filters" v-if="selectedTeams.length > 0 || Object.keys(searchLeadForms).length > 0">
          <div class="selected-filters__filter" v-for="teamId in selectedTeams" :key="`team${teamId}`">
            <span>
              Team: {{
                (loggedUser.teams && loggedUser.teams.find(t => t.id == teamId))
                  ? loggedUser.teams.find(t => t.id == teamId).name
                  : 'Team ' + teamId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeTeam(teamId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="statusId in leadStatuses" :key="`status${statusId}`">
            <span>{{
              (searchLeadForms.customer_sub_statuses && searchLeadForms.customer_sub_statuses.find(item => item.id ==
                statusId))
                ? searchLeadForms.customer_sub_statuses.find(item => item.id == statusId).name
                : '...'
            }}</span>
            <div class="selected-filters__remove" @click="removeLeadStatus(statusId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="categoryId in leadCategories" :key="`category${categoryId}`">
            <span>{{
              (searchLeadForms.branchCategories && searchLeadForms.branchCategories.find(item => item.id ==
                categoryId))
                ? searchLeadForms.branchCategories.find(item => item.id == categoryId)[`long_${language}`]
                : '...'
            }}</span>
            <div class="selected-filters__remove" @click="removeLeadCategory(categoryId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="agentId in selectedAgents" :key="`agent${agentId}`">
            <span>
              {{
                (searchLeadForms.agents && searchLeadForms.agents.find(a => a.id == agentId))
                  ? searchLeadForms.agents.find(a => a.id == agentId).first_name + ' ' +
                  searchLeadForms.agents.find(a => a.id == agentId).last_name
                  : 'Agent ' + agentId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeAgent(agentId)"></div>
          </div>

          <div class="selected-filters__filter" v-if="deadlineFrom || deadlineTo">
            <span>Deadline: <br>{{ formatDateRange(deadlineFrom, deadlineTo) }}</span>
            <div class="selected-filters__remove" @click="clearDeadline()"></div>
          </div>

          <div class="selected-filters__filter" v-if="zipFrom || zipTo">
            <span>{{ $t('searchCustomerFields.zip') }}: {{ formatRange(zipFrom, zipTo) }}</span>
            <div class="selected-filters__remove" @click="clearZip()"></div>
          </div>
        </div>
        <form class="search search--filter" @submit.prevent="searchLeads()">
          <div class="search__wrapper">
            <input class="search__search" ref="search" :class="{ 'search__search--active': searchKeywords }" type="text"
              v-model="searchKeywords" />
            <div class="search__icon"></div>
            <div class="selected-filters__filter search__placeholder" v-if="searchPlaceholder">
              <div class="search__placeholder-content">
                {{ searchPlaceholder }}
                <div class="selected-filters__remove" @click="removeSearch()"></div>
              </div>
            </div>
            <div class="search__splitter"></div>
          </div>
          <div class="search__filter" ref="filterToggler" @click="showSearchFilter = !showSearchFilter"></div>
          <SearchFilter v-if="showSearchFilter" :filterToggler="filterToggler" :searchType="'lead'"
            :toggleSearchFilter="(state) => {
              showSearchFilter = state;
            }
              " />
        </form> 

        <button v-if="checkRolePermission('customer_create')" type="button" class="button button--default import-button"
          @click="showLeadImportModal">
          Import Leads
        </button>

        <button v-if="checkRolePermission('customer_create')" type="button" class="button button--default transfer-button"
          @click="openTransferModal" :disabled="selectedCustomerIds.length === 0">
          Transfer{{ selectedCustomerIds.length > 0 ? ` (${selectedCustomerIds.length})` : '' }}
        </button>

        <button v-if="checkRolePermission('customer_create')" type="button" class="button button--default add-button"
          @click="showCustomerLeadModal">
          <div class="button__icon"></div>
        </button>

      </div>
    </div>
    <div class="customers__content">
      <Loading v-if="leadsLoading" />
      <LeadsTable ref="leadsTable" v-else :customers="leadCustomersPerPage.data || []" @refresh="getLeadCustomers" @selection-changed="handleSelectionChange" />
    </div>
    <Pagination :limit="3" :data="leadCustomersPerPage" @pagination-change-page="getResults"></Pagination>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import LeadsTable from "@/components/LeadsTable";
import Loading from "@/partials/Loading";
import SearchFilter from "@/partials/SearchFilter";

const store = useStore();
const route = useRoute();
const router = useRouter();
const instance = getCurrentInstance();

const searchPlaceholder = ref("");
const searchKeywords = ref("");
const showSearchFilter = ref(false);
const selectedTeams = ref([]);
const selectedAgents = ref([]);
const leadStatuses = ref([]);
const leadCategories = ref([]);
const leadReferals = ref([]);
const deadlineFrom = ref("");
const deadlineTo = ref("");
const zipFrom = ref("");
const zipTo = ref("");
const selectedCustomerIds = ref([]);

// Template refs
const search = ref(null);
const filterToggler = ref(null);
const leadsTable = ref(null);

const leadCustomersPerPage = computed(() => store.getters.leadCustomersPerPage);
const leadsLoading = computed(() => store.getters.leadsLoading);
const searchLeadForms = computed(() => store.getters.searchLeadForms);
const language = computed(() => store.getters.language);
const loggedUser = computed(() => store.getters.loggedUser);
//
const hasActiveFilters = computed(() => {
  const q = route.query;

  return !!(
    q.search ||
    q.teams ||
    q.agents ||
    q.lead_statuses ||
    q.lead_categories ||
    q.lead_referrals ||
    q.deadline_from ||
    q.deadline_to ||
    q.zip_from ||
    q.zip_to
  );
});

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

const formatDateRange = (from, to) => {
  if (!from && !to) return "";
  if (from && !to) return `${formatDate(from)} - ...`;
  if (!from && to) return `... - ${formatDate(to)}`;
  return `${formatDate(from)} - ${formatDate(to)}`;
};

const formatRange = (from, to) => {
  if (!from && !to) return "";
  if (from && !to) return `${from} - ...`;
  if (!from && to) return `... - ${to}`;
  return `${from} - ${to}`;
};

const loadFiltersFromQuery = (q) => {
  searchKeywords.value = q.search || "";
  zipFrom.value = q.zip_from || "";
  zipTo.value = q.zip_to || "";

  selectedTeams.value = q.teams
  ? typeof q.teams === "string"
    ? q.teams.split(",").map(Number)
    : q.teams
  : [];

  if (q.agents) {
    selectedAgents.value = typeof q.agents === "string" ? q.agents.split(",") : q.agents;
  } else {
    selectedAgents.value = [];
  }

  leadStatuses.value = q.lead_statuses
    ? typeof q.lead_statuses === "string"
      ? q.lead_statuses.split(",").map(Number)
      : q.lead_statuses
    : [];

  leadCategories.value = q.lead_categories
    ? typeof q.lead_categories === "string"
      ? q.lead_categories.split(",").map(Number)
      : q.lead_categories
    : [];

  leadReferals.value = q.lead_referrals
    ? typeof q.lead_referrals === "string"
      ? q.lead_referrals.split(",").map(Number)
      : q.lead_referrals
    : [];

  deadlineFrom.value = q.deadline_from || "";
  deadlineTo.value = q.deadline_to || "";
};

const searchLeads = async () => {
  store.commit("setLeadsLoading", true);

  const page = Number(route.query.page) || 1;

  // const teamsFromUrl = route.query.teams;
  // const teams = teamsFromUrl ? teamsFromUrl.split(",").map(Number) : [];

  await store.dispatch("searchLeads", {
    search: searchKeywords.value,
    agents: selectedAgents.value,
    lead_statuses: leadStatuses.value,
    lead_categories: leadCategories.value,
    lead_referrals: leadReferals.value,
    deadline_from: deadlineFrom.value,
    deadline_to: deadlineTo.value,
    zip_from: zipFrom.value,
    zip_to: zipTo.value,
    teams: selectedTeams.value,
    page,
  });

  store.commit("setLeadsLoading", false);
};

const updateQuery = () => {
  router
    .replace({
      query: {
        page: 1,
        search: searchKeywords.value || undefined,
        teams: selectedTeams.value.length ? selectedTeams.value.join(",") : undefined,
        agents: selectedAgents.value.length ? selectedAgents.value.join(",") : undefined,
        lead_statuses: leadStatuses.value.length ? leadStatuses.value.join(",") : undefined,
        lead_categories: leadCategories.value.length ? leadCategories.value.join(",") : undefined,
        lead_referrals: leadReferals.value.length ? leadReferals.value.join(",") : undefined,
        deadline_from: deadlineFrom.value || undefined,
        deadline_to: deadlineTo.value || undefined,
        zip_from: zipFrom.value || undefined,
        zip_to: zipTo.value || undefined,
      },
    })
    .catch(() => {});
};

const getResults = (paginationPage) => {
  if (Number(route.query.page) === Number(paginationPage)) return;

  router
    .replace({
      query: { ...route.query, page: paginationPage },
    })
    .catch(() => {});
};

const clearDeadline = () => {
  deadlineFrom.value = "";
  deadlineTo.value = "";
  updateQuery();
};

const clearZip = () => {
  zipFrom.value = "";
  zipTo.value = "";
  updateQuery();
};

const clearAllDependentFilters = () => {
  selectedAgents.value = [];
  leadStatuses.value = [];
  leadCategories.value = [];
  leadReferals.value = [];
  deadlineFrom.value = "";
  deadlineTo.value = "";
  zipFrom.value = "";
  zipTo.value = "";

  store.commit("setSearchLeadForms", {});
};

const removeTeam = (id) => {
  selectedTeams.value = selectedTeams.value.filter((x) => x !== id);

  if (selectedTeams.value.length === 0) {
    clearAllDependentFilters();
  }

  updateQuery();
};

const removeAgent = (id) => {
  selectedAgents.value = selectedAgents.value.filter((x) => x !== id);
  updateQuery();
};

const removeLeadStatus = (id) => {
  leadStatuses.value = leadStatuses.value.filter((x) => x !== id);
  updateQuery();
};

const removeLeadCategory = (id) => {
  leadCategories.value = leadCategories.value.filter((x) => x !== id);
  updateQuery();
};

const removeSearch = () => {
  searchKeywords.value = "";
  searchPlaceholder.value = "";
  updateQuery();
};

const getLeadCustomers = async () => {
  try {
    const page = Number(route.query.page) || 1;
    await store.dispatch("fetchLeadCustomers", page);
  } catch (error) {
    console.error("Failed to fetch leads:", error);
  }
};

const handleSelectionChange = (selectedIds) => {
  selectedCustomerIds.value = selectedIds.map((id) => parseInt(id, 10));
};

const openTransferModal = () => {
  if (selectedCustomerIds.value.length === 0) {
    return;
  }
  store.commit("setLeadsToTransfer", selectedCustomerIds.value);
  store.commit("setShowTransferLeadsModal", true);

  selectedCustomerIds.value = [];
  
  // Clear table selection
  if (leadsTable.value?.clearSelection) {
    leadsTable.value.clearSelection();
  }
};

const showLeadImportModal = () => {
  store.commit("setShowLeadImportModal", true);
};

const showCustomerLeadModal = () => {
  store.commit("setShowCustomerLeadModal", true);
};

// Helper for permission checking in template
const checkRolePermission = (permission) => {
  return instance?.appContext?.config?.globalProperties?.$checkRolePermission?.(permission) ?? false;
};

// Watch route query changes - FIX: prevent recursive updates
//let isUpdatingQuery = false;

// watch(
//   () => route.query,
//   async (query) => {
//     // Prevent recursive updates
//     if (isUpdatingQuery) {
//       isUpdatingQuery = false;
//       return;
//     }

//     if (!query.page) {
//       isUpdatingQuery = true;
//       router.replace({ query: { ...query, page: 1 } });
//       return;
//     }
    
//     loadFiltersFromQuery(query);
//     await searchLeads();
//   },
//   { immediate: true }
// );

watch(
  () => route.query,
  async (query) => {
    if (!query.page) {
      await router.replace({
        query: {
          ...query,
          page: 1,
        },
      });

      return;
    }

    loadFiltersFromQuery(query);

    if (hasActiveFilters.value) {
      await searchLeads();
    } else {
      await getLeadCustomers();
    }
  },
  { immediate: true }
);

watch(
  () => selectedTeams.value,
  async (teams) => {
    if (teams.length > 0) {
      await store.dispatch("getSearchLeadForms", teams);
    } else {
      store.commit("setSearchLeadForms", {});
    }
  },
  { deep: true }
);

onMounted(async () => {
  const hasAccess = checkRolePermission("customer_access") || checkRolePermission("customer_show");

  if (!hasAccess) {
    router.push("/dashboard");
    return;
  }

  try {
    const queryTeams = route.query.teams ? route.query.teams.split(",").map(Number) : [];

    if (queryTeams.length > 0) {
      await store.dispatch("getSearchLeadForms", queryTeams);
    } else if (loggedUser.value.teams && loggedUser.value.teams.length === 1) {
      await store.dispatch("getSearchLeadForms", [loggedUser.value.teams[0].id]);
    }
  } catch (error) {
    console.error("Error loading lead forms:", error);
  }
});
</script>

<style scoped lang="scss">
.add-button {
  width: 70px !important;

  .button__icon {
    margin: auto;
    width: 24px;
    height: 24px;
    background-image: url("../assets/icons/default/add_icon.svg");
    background-repeat: no-repeat;
    background-position: center;
    transition: all 0.2s ease-in-out;
  }
}

.import-button{
  width: 150px !important;
}

.transfer-button{
  width: 150px !important;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.customers {
  height: 100%;

  &__content {
    margin-bottom: 20px;
    height: calc(100% - 48px - 15px - 16px - 20px);
  }
}
</style>